import { useEffect, useRef } from 'react';

/**
 * Efecto "magnético": el elemento se desplaza unos px hacia el cursor
 * mientras lo sobrevuela. Escribe `transform` directo en el DOM (sin
 * useState) para que pointermove nunca dispare un render de React; el
 * cálculo va detrás de requestAnimationFrame para no exceder un cambio de
 * estilo por frame. Se auto-desactiva en touch (no hay cursor que seguir) y
 * si el usuario pidió menos movimiento.
 */
export function useMagnetic(strength = 0.3, max = 10) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia('(pointer: coarse)').matches) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        let frame = null;
        let targetX = 0;
        let targetY = 0;

        const onMove = (e) => {
            const rect = el.getBoundingClientRect();
            const relX = e.clientX - (rect.left + rect.width / 2);
            const relY = e.clientY - (rect.top + rect.height / 2);
            targetX = Math.max(-max, Math.min(max, relX * strength));
            targetY = Math.max(-max, Math.min(max, relY * strength));
            if (frame) return;
            frame = requestAnimationFrame(() => {
                el.style.transform = `translate3d(${targetX}px, ${targetY}px, 0)`;
                frame = null;
            });
        };

        const onLeave = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = null;
            el.style.transform = '';
        };

        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerleave', onLeave);
        return () => {
            el.removeEventListener('pointermove', onMove);
            el.removeEventListener('pointerleave', onLeave);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [strength, max]);

    return ref;
}
